import React, { useState, useEffect, useMemo } from "react";
import API from "../../../api/api";
import Swal from "sweetalert2";
import {
  FaUsers,
  FaUserShield,
  FaRunning,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaTimes,
  FaSave,
} from "react-icons/fa";

const TeamManagement = ({ activeCamp, selectedCamp, campId: propCampId, onRefreshAll }) => {
  const [teams, setTeams] = useState([]);
  const [allAthletes, setAllAthletes] = useState([]);
  const [allCoaches, setAllCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modals state
  const [createTeamModal, setCreateTeamModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamCoaches, setNewTeamCoaches] = useState([]);
  const [newTeamAthletes, setNewTeamAthletes] = useState([]);

  const [manageCoachesModal, setManageCoachesModal] = useState(null); // team object
  const [selectedCoachIds, setSelectedCoachIds] = useState([]);

  const [manageAthletesModal, setManageAthletesModal] = useState(null); // team object
  const [selectedAthleteIds, setSelectedAthleteIds] = useState([]);
  const [athleteSearchFilter, setAthleteSearchFilter] = useState("");

  const [campId, setCampId] = useState(propCampId || activeCamp?.id || selectedCamp?.id || null);

  useEffect(() => {
    const cid = propCampId || activeCamp?.id || selectedCamp?.id;
    if (cid) {
      setCampId(cid);
    } else {
      API.camp.getActiveCamp().then((res) => {
        const id = res?.data?.camp?.id || res?.camp?.id;
        if (id) setCampId(id);
      }).catch(() => {});
    }
  }, [propCampId, activeCamp, selectedCamp]);

  const fetchTeams = async (targetCampId) => {
    const id = targetCampId || campId || propCampId || activeCamp?.id || selectedCamp?.id;
    setLoading(true);
    try {
      const res = await API.camp.getTeams({ camp_id: id || undefined });
      setTeams(res?.data?.teams || res?.teams || []);
      setAllAthletes(res?.data?.all_athletes || res?.all_athletes || []);
      setAllCoaches(res?.data?.all_coaches || res?.all_coaches || []);
    } catch (err) {
      console.error("Failed to load teams:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams(campId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campId]);

  // Filtered teams
  const filteredTeams = useMemo(() => {
    if (!searchTerm.trim()) return teams;
    const term = searchTerm.toLowerCase();
    return teams.filter(
      (t) =>
        t.team_name.toLowerCase().includes(term) ||
        (t.coaches && t.coaches.some((c) => c.name.toLowerCase().includes(term))) ||
        (t.disciplines && t.disciplines.some((d) => d.toLowerCase().includes(term)))
    );
  }, [teams, searchTerm]);

  // Create Team
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) {
      Swal.fire("Required", "Please enter a team name.", "warning");
      return;
    }
    const resolvedCampId = campId || propCampId || activeCamp?.id || selectedCamp?.id || 1;
    try {
      await API.camp.createTeam({
        camp_id: resolvedCampId,
        team_name: newTeamName.trim(),
        coach_ids: newTeamCoaches,
        athlete_ids: newTeamAthletes,
      });
      Swal.fire("Success", `Team '${newTeamName}' created successfully!`, "success");
      setCreateTeamModal(false);
      setNewTeamName("");
      setNewTeamCoaches([]);
      setNewTeamAthletes([]);
      fetchTeams(resolvedCampId);
      if (onRefreshAll) onRefreshAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  // Open Manage Coaches Modal
  const openManageCoaches = (team) => {
    setManageCoachesModal(team);
    setSelectedCoachIds((team.coaches || []).map((c) => c.id));
  };

  // Save Manage Coaches
  const handleSaveCoaches = async () => {
    if (!manageCoachesModal) return;
    if (selectedCoachIds.length === 0) {
      Swal.fire("Required", "Please select at least one coach for the team.", "warning");
      return;
    }
    const resolvedCampId = campId || propCampId || activeCamp?.id || selectedCamp?.id || 1;
    try {
      await API.camp.updateTeamCoaches({
        camp_id: resolvedCampId,
        team_name: manageCoachesModal.team_name,
        coach_ids: selectedCoachIds,
      });
      Swal.fire("Success", `Coaches for team '${manageCoachesModal.team_name}' updated!`, "success");
      setManageCoachesModal(null);
      fetchTeams(resolvedCampId);
      if (onRefreshAll) onRefreshAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  // Open Manage Athletes Modal
  const openManageAthletes = (team) => {
    setManageAthletesModal(team);
    setSelectedAthleteIds((team.athletes || []).map((a) => a.id));
    setAthleteSearchFilter("");
  };

  // Save Manage Athletes
  const handleSaveAthletes = async () => {
    if (!manageAthletesModal) return;
    const resolvedCampId = campId || propCampId || activeCamp?.id || selectedCamp?.id || 1;
    try {
      await API.camp.updateTeamAthletes({
        camp_id: resolvedCampId,
        team_name: manageAthletesModal.team_name,
        athlete_ids: selectedAthleteIds,
      });
      Swal.fire("Success", `Athletes for team '${manageAthletesModal.team_name}' updated!`, "success");
      setManageAthletesModal(null);
      fetchTeams(resolvedCampId);
      if (onRefreshAll) onRefreshAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  // Disband Team
  const handleDisbandTeam = async (teamName) => {
    const result = await Swal.fire({
      title: "Disband Team?",
      text: `Are you sure you want to disband team '${teamName}'? Athletes and coaches will be unassigned from this team.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Disband",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });

    if (!result.isConfirmed) return;

    const resolvedCampId = campId || propCampId || activeCamp?.id || selectedCamp?.id || 1;
    try {
      await API.camp.disbandTeam({
        camp_id: resolvedCampId,
        team_name: teamName,
      });
      Swal.fire("Disbanded", `Team '${teamName}' has been disbanded.`, "success");
      fetchTeams(resolvedCampId);
      if (onRefreshAll) onRefreshAll();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <FaUsers className="text-cyan-700" />
            Team &amp; Squad Management
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Manage teams, assign Assistant Coaches (Assistant Coach 1, Assistant Coach 2), and allocate athletes with separate dedicated controls.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setCreateTeamModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer"
          >
            <FaPlus /> Create New Team
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
          <input
            type="text"
            placeholder="Search teams by name, coach, or discipline..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:bg-white transition-all"
          />
        </div>

        <div className="flex items-center gap-2 text-xs text-slate-600 font-bold">
          <span className="bg-cyan-50 text-cyan-800 px-3 py-1.5 rounded-lg border border-cyan-100">
            Total Teams: <strong>{teams.length}</strong>
          </span>
          <span className="bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-lg border border-emerald-100">
            Assigned Athletes:{" "}
            <strong>
              {teams.reduce((acc, t) => acc + (t.athletes_count || 0), 0)}
            </strong>
          </span>
        </div>
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <p className="text-sm font-bold text-slate-500 animate-pulse">Loading teams data...</p>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 space-y-3">
          <FaUsers className="mx-auto text-3xl text-slate-300" />
          <p className="text-sm font-bold text-slate-600">No teams created yet</p>
          <p className="text-xs text-slate-400">Click &quot;Create New Team&quot; above to organize coaches and athletes into teams.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filteredTeams.map((team) => (
            <div
              key={team.team_name}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col justify-between"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-slate-900 to-cyan-950 p-4 text-white flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Team / Squad</div>
                  <h3 className="text-base font-black tracking-tight">{team.team_name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {team.disciplines && team.disciplines.length > 0 && (
                    <span className="px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-lg text-[11px] font-bold text-cyan-200">
                      {team.disciplines.join(", ")}
                    </span>
                  )}
                  <button
                    onClick={() => handleDisbandTeam(team.team_name)}
                    className="p-1.5 hover:bg-rose-500/20 text-rose-300 hover:text-rose-200 rounded-lg transition-colors cursor-pointer"
                    title="Disband Team"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-4 flex-1">
                {/* 1. Coaches Section */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                      <FaUserShield className="text-cyan-700" />
                      Assistant Coaches ({team.coaches_count || 0})
                    </span>
                    <button
                      onClick={() => openManageCoaches(team)}
                      className="text-[11px] font-bold text-cyan-700 hover:text-cyan-900 flex items-center gap-1 cursor-pointer bg-cyan-50 px-2 py-0.5 rounded-lg border border-cyan-100 hover:bg-cyan-100 transition-colors"
                    >
                      <FaEdit /> Edit Assistant Coaches
                    </button>
                  </div>

                  {team.coaches && team.coaches.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {team.coaches.map((coach, cIdx) => (
                        <div
                          key={coach.id}
                          className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 flex items-center gap-2.5"
                        >
                          <div className="w-8 h-8 rounded-full bg-cyan-700 text-white font-bold text-xs flex items-center justify-center shrink-0 overflow-hidden">
                            {coach.photo ? (
                              <img src={coach.photo} alt={coach.name} className="w-full h-full object-cover" />
                            ) : (
                              coach.name.charAt(0)
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold text-slate-900 truncate">{coach.name}</div>
                            <div className="text-[10px] text-cyan-800 font-semibold truncate">
                              {cIdx === 0 ? "Assistant Coach 1" : (cIdx === 1 ? "Assistant Coach 2" : `Assistant Coach ${cIdx + 1}`)}
                            </div>
                            {coach.mobile && (
                              <div className="text-[10px] text-slate-500 truncate">{coach.mobile}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs font-medium">
                      ⚠️ No assistant coach assigned to this team yet. Click &quot;Edit Assistant Coaches&quot; to assign.
                    </div>
                  )}
                </div>

                {/* 2. Athletes Section */}
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                      <FaRunning className="text-emerald-700" />
                      Assigned Athletes ({team.athletes_count || 0})
                    </span>
                    <button
                      onClick={() => openManageAthletes(team)}
                      className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1 cursor-pointer bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100 hover:bg-emerald-100 transition-colors"
                    >
                      <FaEdit /> Edit Athletes
                    </button>
                  </div>

                  {team.athletes && team.athletes.length > 0 ? (
                    <div className="max-h-36 overflow-y-auto space-y-1.5 pr-1">
                      {team.athletes.map((ath, aIdx) => (
                        <div
                          key={ath.id}
                          className="bg-slate-50/70 border border-slate-200/80 rounded-lg px-2.5 py-1.5 text-xs flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <span className="font-mono text-[11px] font-bold text-slate-400">
                              #{aIdx + 1}
                            </span>
                            <span className="font-bold text-slate-800 truncate">{ath.name}</span>
                            <span className="text-[10px] font-mono text-cyan-800 bg-cyan-100/70 px-1.5 rounded">
                              {ath.bib_no}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 shrink-0">
                            <span>{ath.category || ath.discipline_name}</span>
                            {ath.room_no && (
                              <span className="bg-slate-200 text-slate-700 px-1.5 rounded font-semibold">
                                Room {ath.room_no}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 text-xs text-center">
                      No athletes enrolled in this team yet. Click &quot;Edit Athletes&quot; to add.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* =========================================================
          MODAL 1: CREATE NEW TEAM
      ========================================================= */}
      {createTeamModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="bg-gradient-to-r from-slate-900 to-cyan-950 p-4 text-white flex items-center justify-between">
              <h3 className="text-base font-black flex items-center gap-2">
                <FaUsers className="text-cyan-400" /> Create New Team
              </h3>
              <button
                onClick={() => setCreateTeamModal(false)}
                className="text-white/70 hover:text-white p-1 rounded-lg"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleCreateTeam} className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Team Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Athletics Team Alpha, Wheelchair Tennis Squad..."
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  className="w-full px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:bg-white"
                />
              </div>

              {/* Initial Coaches Select */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Assign Assistant Coaches (Select one or more)
                </label>
                <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50 space-y-1">
                  {allCoaches.map((c) => {
                    const isChecked = newTeamCoaches.includes(c.id);
                    return (
                      <label
                        key={c.id}
                        className={`flex items-center gap-2 p-1.5 rounded-lg text-xs cursor-pointer ${
                          isChecked ? "bg-cyan-100/70 font-bold text-cyan-900" : "hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewTeamCoaches([...newTeamCoaches, c.id]);
                            } else {
                              setNewTeamCoaches(newTeamCoaches.filter((id) => id !== c.id));
                            }
                          }}
                          className="rounded text-cyan-700 focus:ring-cyan-500"
                        />
                        <span>{c.name} {c.district ? `(${c.district})` : ""}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Initial Athletes Select */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Enroll Initial Athletes (Optional)
                </label>
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50 space-y-1">
                  {allAthletes.map((a) => {
                    const isChecked = newTeamAthletes.includes(a.id);
                    return (
                      <label
                        key={a.id}
                        className={`flex items-center justify-between gap-2 p-1.5 rounded-lg text-xs cursor-pointer ${
                          isChecked ? "bg-emerald-100/70 font-bold text-emerald-900" : "hover:bg-slate-100 text-slate-700"
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewTeamAthletes([...newTeamAthletes, a.id]);
                              } else {
                                setNewTeamAthletes(newTeamAthletes.filter((id) => id !== a.id));
                              }
                            }}
                            className="rounded text-emerald-700 focus:ring-emerald-500"
                          />
                          <span className="font-mono text-[10px] text-slate-500 shrink-0">#{a.bib_no || "—"}</span>
                          <span className="truncate">{a.name} ({a.category || a.discipline_name || "General"})</span>
                        </div>
                        {a.current_team_name && (
                          <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 shrink-0">
                            In {a.current_team_name}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setCreateTeamModal(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl text-xs font-bold shadow transition-colors"
                >
                  Create Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL 2: MANAGE COACHES OF TEAM
      ========================================================= */}
      {manageCoachesModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden flex flex-col max-h-[85vh]">
            <div className="bg-cyan-900 p-4 text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold text-cyan-200">Manage Team Assistant Coaches</div>
                <h3 className="text-base font-black">{manageCoachesModal.team_name}</h3>
              </div>
              <button
                onClick={() => setManageCoachesModal(null)}
                className="text-white/70 hover:text-white p-1 rounded-lg"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <p className="text-xs text-slate-500">
                Select assistant coaches to assign to this team (Assistant Coach 1 &amp; Assistant Coach 2). Both coaches serve as equal Assistant Coaches.
              </p>

              <div className="space-y-1.5 border border-slate-200 rounded-xl p-2 bg-slate-50 max-h-60 overflow-y-auto">
                {allCoaches.map((c) => {
                  const isChecked = selectedCoachIds.includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2.5 p-2 rounded-lg text-xs cursor-pointer transition-colors ${
                        isChecked ? "bg-cyan-100/80 font-bold text-cyan-900 border border-cyan-200" : "hover:bg-slate-100 text-slate-800"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCoachIds([...selectedCoachIds, c.id]);
                          } else {
                            setSelectedCoachIds(selectedCoachIds.filter((id) => id !== c.id));
                          }
                        }}
                        className="rounded text-cyan-700 focus:ring-cyan-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{c.name}</div>
                        <div className="text-[10px] text-slate-500">{c.district || "District Not Specified"} &bull; {c.mobile}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600">
                {selectedCoachIds.length} Assistant Coach{selectedCoachIds.length !== 1 ? "es" : ""} Selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setManageCoachesModal(null)}
                  className="px-3.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCoaches}
                  className="px-4 py-1.5 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
                >
                  <FaSave /> Save Assistant Coaches
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          MODAL 3: MANAGE ATHLETES OF TEAM
      ========================================================= */}
      {manageAthletesModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]">
            <div className="bg-emerald-900 p-4 text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold text-emerald-200">Manage Team Athletes</div>
                <h3 className="text-base font-black">{manageAthletesModal.team_name}</h3>
              </div>
              <button
                onClick={() => setManageAthletesModal(null)}
                className="text-white/70 hover:text-white p-1 rounded-lg"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <p className="text-xs text-slate-500">
                All camp athletes are listed below with their current allotment status. Selecting an athlete assigned to another team will transfer them to <strong>{manageAthletesModal.team_name}</strong> and remove them from their previous team upon saving.
              </p>

              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  placeholder="Filter athletes by name, BIB, category, district, or current team..."
                  value={athleteSearchFilter}
                  onChange={(e) => setAthleteSearchFilter(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="space-y-1.5 border border-slate-200 rounded-xl p-2 bg-slate-50 max-h-72 overflow-y-auto">
                {allAthletes.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-xs font-semibold">
                    No athletes found in this camp.
                  </div>
                ) : (
                  allAthletes
                    .filter((a) => {
                      if (!athleteSearchFilter.trim()) return true;
                      const q = athleteSearchFilter.toLowerCase();
                      return (
                        a.name?.toLowerCase().includes(q) ||
                        String(a.bib_no || "").toLowerCase().includes(q) ||
                        a.category?.toLowerCase().includes(q) ||
                        a.discipline_name?.toLowerCase().includes(q) ||
                        a.district?.toLowerCase().includes(q) ||
                        a.current_team_name?.toLowerCase().includes(q)
                      );
                    })
                    .map((a) => {
                      const isChecked = selectedAthleteIds.includes(a.id);
                      const isCurrentTeam = a.current_team_name === manageAthletesModal.team_name;
                      const isOtherTeam = Boolean(a.current_team_name && a.current_team_name !== manageAthletesModal.team_name);

                      return (
                        <label
                          key={a.id}
                          className={`flex items-center gap-2.5 p-2 rounded-lg text-xs cursor-pointer transition-all border ${
                            isChecked
                              ? isOtherTeam
                                ? "bg-amber-50/90 font-bold text-amber-950 border-amber-300 shadow-xs"
                                : "bg-emerald-50/90 font-bold text-emerald-950 border-emerald-300 shadow-xs"
                              : "hover:bg-slate-100 text-slate-800 border-transparent"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAthleteIds([...selectedAthleteIds, a.id]);
                              } else {
                                setSelectedAthleteIds(selectedAthleteIds.filter((id) => id !== a.id));
                              }
                            }}
                            className="rounded text-emerald-700 focus:ring-emerald-500"
                          />
                          <span className="font-mono text-[10px] text-cyan-800 bg-white px-1.5 py-0.5 rounded border border-slate-200 font-bold shrink-0">
                            #{a.bib_no || "—"}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold">{a.name}</span>
                              <span className="text-[10px] text-slate-500">
                                ({a.category || a.discipline_name || "Discipline Pending"} &bull; {a.district || "District N/A"})
                              </span>
                            </div>
                            {/* Allotment Status Badge */}
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              {isCurrentTeam ? (
                                <span className="px-1.5 py-0.2 rounded text-[9.5px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                  Currently in this team
                                </span>
                              ) : isOtherTeam ? (
                                <span className={`px-1.5 py-0.2 rounded text-[9.5px] font-bold border ${
                                  isChecked 
                                    ? "bg-amber-200 text-amber-950 border-amber-400" 
                                    : "bg-amber-100/80 text-amber-800 border-amber-200"
                                }`}>
                                  Assigned to: {a.current_team_name} {isChecked ? "➔ (Will transfer to this team)" : ""}
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.2 rounded text-[9.5px] font-semibold bg-slate-200/80 text-slate-600">
                                  Unassigned
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600">
                {selectedAthleteIds.length} Athlete{selectedAthleteIds.length !== 1 ? "s" : ""} Selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setManageAthletesModal(null)}
                  className="px-3.5 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAthletes}
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow flex items-center gap-1.5"
                >
                  <FaSave /> Save Athletes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { TeamManagement };
export default TeamManagement;
